import {
    isValidElement,
    type ReactNode,
    cloneElement,
    Children,
    Fragment
} from "react";
import type {
    AccordionOrderedTriggersConstantType,
    AccordionChangeValueConstantType,
    AccordionNextValuesConstantType,
    AccordionNavigationConstantType,
    AccordionLayoutConstantType,
    AccordionValuesConstantType,
    AccordionOrderedTrigger,
    AccordionAttributeNode,
    AccordionFlattenProps,
    AccordionLayout,
    AccordionValue
} from "./type";

export const ACCORDION_NAVIGATION_KEYS: Array<string> = [
    "ArrowDown",
    "ArrowUp",
    "Home",
    "End"
];

export const ACCORDION_REGION_MAX_ITEMS = 6;

const ACCORDION_DOCUMENT_POSITION_FOLLOWING = 4;

const getIsAccordionItem = (node: ReactNode): boolean => {
    if(!isValidElement(node)) {
        return false;
    }

    const elementType: unknown = node.type;

    return typeof elementType === "function" && "accordionPart" in elementType && elementType.accordionPart === "item";
};

export const getIsAccordionAttributeNode = (value: unknown): value is AccordionAttributeNode => {
    return typeof value === "object" && value !== null && "setAttribute" in value && typeof value.setAttribute === "function" && "removeAttribute" in value && typeof value.removeAttribute === "function";
};

const getIsDocumentNode = (value: unknown): value is Node => {
    return typeof value === "object" && value !== null && "compareDocumentPosition" in value && typeof value.compareDocumentPosition === "function";
};

const flattenAccordionChildren = (children: ReactNode, prefix: string = ""): Array<ReactNode> => {
    const nodes: Array<ReactNode> = [];

    Children.toArray(children).forEach((child, index) => {
        if(isValidElement<AccordionFlattenProps>(child) && child.type === Fragment) {
            nodes.push(...flattenAccordionChildren(child.props.children, `${prefix}${index}.`));

            return;
        }

        if(!prefix || !isValidElement(child)) {
            nodes.push(child);

            return;
        }

        nodes.push(cloneElement(child, {
            key: `${prefix}${child.key ?? index}`
        }));
    });

    return nodes;
};

export const getAccordionLayout = ({
    children
}: AccordionLayoutConstantType): AccordionLayout => {
    const nodes = flattenAccordionChildren(children);

    const itemIndexes: Array<number> = [];

    nodes.forEach((node, index) => {
        if(getIsAccordionItem(node)) {
            itemIndexes.push(index);
        }
    });

    const firstIndex = itemIndexes[0];

    const lastIndex = itemIndexes[itemIndexes.length - 1];

    return {
        items: nodes.map((node, index) => ({
            layout: {
                isFirst: index === firstIndex,
                isLast: index === lastIndex
            },
            node
        })),
        itemCount: itemIndexes.length
    };
};

export const getAccordionValues = ({
    expandBehaviour,
    value
}: AccordionValuesConstantType): Array<string> => {
    if(!value) {
        return [];
    }

    const values = Array.isArray(value) ? value : [
        value
    ];

    return expandBehaviour === "single" ? values.slice(0, 1) : values;
};

export const getHasAccordionExtraValues = ({
    expandBehaviour,
    value
}: AccordionValuesConstantType): boolean => {
    return expandBehaviour === "single" && Array.isArray(value) && value.length > 1;
};

export const getAccordionNextValues = ({
    expandBehaviour,
    isCollapsible,
    openValues,
    itemValue
}: AccordionNextValuesConstantType): Array<string> | undefined => {
    const isItemOpen = openValues.includes(itemValue);

    if(expandBehaviour === "multiple") {
        return isItemOpen ? openValues.filter((openValue) => openValue !== itemValue) : [
            ...openValues,
            itemValue
        ];
    }

    if(!isItemOpen) {
        return [
            itemValue
        ];
    }

    return isCollapsible ? [] : undefined;
};

export const getAccordionChangeValue = ({
    expandBehaviour,
    values
}: AccordionChangeValueConstantType): AccordionValue => {
    if(expandBehaviour === "multiple") {
        return values;
    }

    return values[0] ?? "";
};

export const getAccordionNavigationIndex = ({
    current,
    count,
    key
}: AccordionNavigationConstantType): number => {
    if(!count) {
        return -1;
    }

    if(key === "Home") {
        return 0;
    }

    if(key === "End") {
        return count - 1;
    }

    if(current < 0) {
        return key === "ArrowUp" ? count - 1 : 0;
    }

    if(key === "ArrowDown") {
        return (current + 1) % count;
    }

    if(key === "ArrowUp") {
        return (current - 1 + count) % count;
    }

    return -1;
};

export const getAccordionOrderedTriggers = ({
    triggers
}: AccordionOrderedTriggersConstantType): Array<AccordionOrderedTrigger> => {
    const orderedTriggers: Array<AccordionOrderedTrigger> = [];

    triggers.forEach((entry, id) => {
        if(!entry.isDisabled) {
            orderedTriggers.push({
                ref: entry.ref,
                id
            });
        }
    });

    return orderedTriggers.sort((first, second) => {
        const firstNode: unknown = first.ref.current;

        const secondNode: unknown = second.ref.current;

        if(!getIsDocumentNode(firstNode) || !getIsDocumentNode(secondNode)) {
            return 0;
        }

        return firstNode.compareDocumentPosition(secondNode) & ACCORDION_DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
};
